import React, { useContext, useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Snackbar,
  Typography,
} from '@mui/material';
import {
  Close as CloseIcon,
  Download as DownloadIcon,
  EditCalendarOutlined as PlanIcon,
  EventBusy as EventBusyIcon,
  HelpOutline as HelpIcon,
  LightbulbOutlined as LightbulbIcon,
} from '@mui/icons-material';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';
import { UserContext } from '../components/MiniDrawer';
import MonthPicker from '../components/monthly/MonthPicker';
import PageHeader, { PageContainer } from '../components/PageHeader';
import MyMonthTable, { draftKey } from '../components/monthly/MyMonthTable';
import { barButtonSx, pillSx } from '../components/monthly/MultiSelectFilter';
import { buildMonthDays, formatHours, MONTH_NAMES } from '../components/monthly/format';
import { STATUS_STYLE, summarizePerson } from '../components/monthly/completeness';
import { invalidatePeopleMonth, loadPeopleMonthBundle, peekPeopleMonthBundle } from '../utils/monthlyPeopleService';
import { fetchUserProjects, fetchWorklogsInRange } from '../utils/worklogService';
import { hoursToMinutes, upsertForecastCell } from '../utils/forecastService';
import { deleteAbsence } from '../utils/absenceService';
import AbsenceDaysDialog from '../components/daily/AbsenceDaysDialog';
import { parseLocalDate } from '../utils/dateHelpers';
import { GREEN } from '../components/home/homeTheme';

const EMPTY = [];
// Stessa chiave della pagina classica: chi ha già chiuso la guida non la rivede
const GUIDE_DISMISSED_KEY = 'runtimesheet_forecast_help_dismissed';
const FORECAST = 'var(--rt-forecast)';

function readGuideDismissed() {
  try {
    return localStorage.getItem(GUIDE_DISMISSED_KEY) === '1';
  } catch (e) {
    return false;
  }
}

function Legend({ color, label }) {
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
      <Box sx={{ width: 14, height: 14, borderRadius: 0.5, bgcolor: color, border: '1px solid var(--rt-hairline)' }} />
      <Typography variant="caption">{label}</Typography>
    </Box>
  );
}

/**
 * My month V2: ore dell'utente per progetto e giorno, con lo stesso linguaggio delle viste Projects/People overview.
 * Mantiene la gestione del forecast (giorni futuri), le assenze e l'export dei worklog del mese.
 */
export default function MyMonthV2() {
  const { selectedUser } = useContext(UserContext);
  const uid = selectedUser && selectedUser !== 'ALL' ? selectedUser : null;
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  // ---- Periodo e modalità (nell'URL, compatibili con i link della pagina classica) ----
  const now = new Date();
  const year = Number(searchParams.get('year')) || now.getFullYear();
  const month = Number(searchParams.get('month')) || now.getMonth() + 1;
  const planning = searchParams.get('mode') === 'forecast';
  const yearMonth = `${year}-${String(month).padStart(2, '0')}`;
  const monthDays = useMemo(() => buildMonthDays(year, month), [year, month]);
  const setParams = (patch) => {
    const next = new URLSearchParams(searchParams);
    Object.entries(patch).forEach(([k, v]) => (v == null ? next.delete(k) : next.set(k, String(v))));
    setSearchParams(next, { replace: true });
  };

  // ---- Dati: ore e assenze del mese (cache condivisa con Home e vista PM), progetti assegnati ----
  const [refreshToken, setRefreshToken] = useState(0);
  const [bundle, setBundle] = useState({ key: null, data: null });
  const [error, setError] = useState(null);
  const [assigned, setAssigned] = useState(EMPTY);
  const ids = useMemo(() => (uid ? [uid] : []), [uid]);
  const currentKey = `${uid}|${yearMonth}|${refreshToken}`;
  const monthData = bundle.key === currentKey ? bundle.data : uid ? peekPeopleMonthBundle(ids, yearMonth) : null;
  const loading = !!uid && !monthData && !error;

  useEffect(() => {
    if (!uid) return undefined;
    let cancelled = false;
    setError(null);
    loadPeopleMonthBundle(ids, yearMonth)
      .promise.then((data) => {
        if (cancelled) return;
        setBundle({ key: currentKey, data });
        // Mese precedente e successivo pronti in background
        [-1, 1].forEach((delta) => {
          const d = new Date(year, month - 1 + delta, 1);
          loadPeopleMonthBundle(ids, `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`).promise.catch(() => {});
        });
      })
      .catch((e) => !cancelled && setError(`Unable to load your month: ${e.message}`));
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentKey]);

  useEffect(() => {
    if (!uid) return;
    fetchUserProjects(uid)
      .then(setAssigned)
      .catch(() => setAssigned(EMPTY));
  }, [uid]);

  const person = monthData?.filtered?.[String(uid)];
  const absences = monthData?.absences?.[String(uid)];

  // Righe: progetti assegnati (attivi) più quelli con ore nel mese
  const rows = useMemo(() => {
    const map = new Map();
    assigned
      .filter((p) => p.active !== false)
      .forEach((p) => map.set(String(p.id), { id: p.id, name: p.name || 'Unknown project', customer: p.customer?.name || '', days: {} }));
    Object.entries(person?.projects || {}).forEach(([pid, p]) => {
      const row = map.get(pid) || { id: Number(pid) || pid, name: p.name, customer: '', days: {} };
      map.set(pid, { ...row, days: p.days });
    });
    return [...map.values()].sort((a, b) => a.name.localeCompare(b.name));
  }, [assigned, person]);

  const summary = useMemo(() => summarizePerson(person?.days, absences, monthDays), [person, absences, monthDays]);

  // ---- Forecast: bozze dei giorni futuri, salvate tutte insieme ----
  const baseline = useMemo(() => {
    if (!planning) return {};
    const b = {};
    rows.forEach((r) =>
      monthDays.forEach((d) => {
        if (d.future) b[draftKey(r.id, d.day)] = r.days[d.day]?.forecast ? formatHours(r.days[d.day].forecast) : '';
      }),
    );
    return b;
  }, [planning, rows, monthDays]);
  const [drafts, setDrafts] = useState({});
  useEffect(() => setDrafts(baseline), [baseline]);
  const changes = Object.keys(baseline).filter((k) => (drafts[k] ?? '') !== (baseline[k] ?? ''));
  const futureDays = monthDays.filter((d) => d.future).length;
  const forecastAhead = rows.reduce(
    (sum, r) =>
      sum +
      monthDays.reduce((s, d) => {
        if (!d.future) return s;
        const draft = planning ? drafts[draftKey(r.id, d.day)] : undefined;
        return s + (draft !== undefined ? hoursToMinutes(draft) : r.days[d.day]?.forecast || 0);
      }, 0),
    0,
  );

  const [saving, setSaving] = useState(false);
  const [snack, setSnack] = useState(null);
  const [pendingAction, setPendingAction] = useState(null);
  const [guideOpen, setGuideOpen] = useState(() => !readGuideDismissed());

  // Con modifiche non salvate, cambiare mese o uscire dalla pianificazione chiede conferma
  const guarded = (action) => (changes.length ? setPendingAction(() => action) : action());
  const changeMonth = (y, m) => guarded(() => setParams({ year: y, month: m }));
  const togglePlanning = () => (planning ? guarded(() => setParams({ mode: null })) : setParams({ mode: 'forecast' }));
  const dismissGuide = () => {
    setGuideOpen(false);
    try {
      localStorage.setItem(GUIDE_DISMISSED_KEY, '1');
    } catch (e) {
      // la guida tornerà alla prossima visita
    }
  };

  const saveForecast = async () => {
    if (!uid || !changes.length) return;
    setSaving(true);
    try {
      await Promise.all(
        changes.map((key) => {
          const [pid, day] = key.split('|');
          return upsertForecastCell({
            userId: Number(uid),
            projectId: Number(pid),
            date: `${yearMonth}-${String(day).padStart(2, '0')}`,
            effortMinutes: hoursToMinutes(drafts[key]),
          });
        }),
      );
      invalidatePeopleMonth(ids, yearMonth);
      setRefreshToken((t) => t + 1);
      setSnack(`Forecast saved (${changes.length} cell${changes.length === 1 ? '' : 's'})`);
    } catch (e) {
      setSnack(e.message || 'Unable to save the forecast');
    } finally {
      setSaving(false);
    }
  };

  // ---- Assenze ----
  const [absenceToDelete, setAbsenceToDelete] = useState(null);
  const [deletingAbsence, setDeletingAbsence] = useState(false);
  const [absenceDaysOpen, setAbsenceDaysOpen] = useState(false);
  const absenceDaysSaved = ({ months, complete, message }) => {
    months.forEach((ym) => invalidatePeopleMonth(ids, ym));
    setRefreshToken((t) => t + 1);
    if (complete) {
      setAbsenceDaysOpen(false);
      setSnack(message);
    }
  };
  const absenceList = absences ? [...absences.values()].sort((a, b) => String(a.date).localeCompare(String(b.date))) : EMPTY;
  const confirmDeleteAbsence = async () => {
    setDeletingAbsence(true);
    try {
      await deleteAbsence(absenceToDelete.id);
      invalidatePeopleMonth(ids, yearMonth);
      setRefreshToken((t) => t + 1);
      setSnack('Absence removed');
      setAbsenceToDelete(null);
    } catch (e) {
      setSnack(`Unable to remove the absence: ${e.message}`);
    } finally {
      setDeletingAbsence(false);
    }
  };

  // ---- Export: worklog del mese, stesse colonne della pagina classica ----
  const [exporting, setExporting] = useState(false);
  const exportMonth = async () => {
    setExporting(true);
    try {
      const last = monthDays.length;
      const worklogs = await fetchWorklogsInRange(uid, `${yearMonth}-01`, `${yearMonth}-${String(last).padStart(2, '0')}`);
      if (!worklogs.length) {
        setSnack('No worklogs to export for this month');
        return;
      }
      const sheet = XLSX.utils.json_to_sheet(
        worklogs.map((w) => ({
          Project: w.project_name || '',
          'Ticket ID': w.ticket_id || '',
          Date: w.date ? parseLocalDate(String(w.date).slice(0, 10)).toLocaleDateString('en-GB') : '',
          'Effort in minutes': w.effort || '',
          Activity: w.activity_name || '',
          Status: w.status_name || '',
          'Effort Type': w.effort_type_name || '',
          User: w.user_name || '',
          Description: w.description || '',
        })),
      );
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, sheet, 'Worklogs');
      const buffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
      saveAs(new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' }), `Worklogs_${MONTH_NAMES[month - 1]}_${year}.xlsx`);
    } catch (e) {
      setSnack(`Export failed: ${e.message}`);
    } finally {
      setExporting(false);
    }
  };

  const openDay = (dateKey) => navigate(`/daily-tracking?date=${dateKey}`);

  if (!uid) return <Alert severity="info">Waiting for authentication to load...</Alert>;

  return (
    <PageContainer>
      <PageHeader title={planning ? 'Plan forecast' : 'My month'}>
        <MonthPicker year={year} month={month} onChange={changeMonth} />
      </PageHeader>

      {/* Azioni */}
      <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 1, mb: 1.5 }}>
        <Button
          onClick={togglePlanning}
          startIcon={planning ? <CloseIcon /> : <PlanIcon />}
          sx={{ ...pillSx(planning), px: 1.5, textTransform: 'none', fontWeight: 700, color: FORECAST, borderColor: planning ? FORECAST : 'var(--rt-pill-border)', bgcolor: planning ? 'var(--rt-forecast-soft)' : 'var(--rt-paper)' }}
        >
          {planning ? 'Done planning' : 'Plan forecast'}
        </Button>
        {planning && (
          <IconButton size="small" onClick={() => setGuideOpen(true)} aria-label="Show forecast guide">
            <HelpIcon fontSize="small" />
          </IconButton>
        )}
        <Box sx={{ flexGrow: 1 }} />
        <Button variant="outlined" color="inherit" startIcon={<EventBusyIcon fontSize="small" />} onClick={() => setAbsenceDaysOpen(true)} sx={barButtonSx}>
          Absences
        </Button>
        <Button
          variant="outlined"
          color="inherit"
          startIcon={exporting ? <CircularProgress size={14} color="inherit" /> : <DownloadIcon fontSize="small" />}
          onClick={exportMonth}
          disabled={exporting || loading}
          sx={barButtonSx}
        >
          Export worklogs
        </Button>
      </Box>

      {planning && (
        <Collapse in={guideOpen}>
          <Alert
            severity="info"
            icon={<LightbulbIcon fontSize="inherit" />}
            sx={{ mb: 1.5 }}
            action={
              <IconButton size="small" onClick={dismissGuide} aria-label="Dismiss guide">
                <CloseIcon fontSize="small" />
              </IconButton>
            }
          >
            Type the hours you expect to work in the <strong>future days</strong> of each project. Totals update as you type; nothing is saved until you press{' '}
            <strong>Save forecast</strong>. An empty cell removes the forecast for that day.
          </Alert>
        </Collapse>
      )}
      {planning && !loading && futureDays === 0 && (
        <Alert severity="warning" sx={{ mb: 1.5 }}>
          This month has no future days left: move to a later month to plan ahead.
        </Alert>
      )}

      {/* Sintesi del mese */}
      {!loading && monthData && (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
          <strong style={{ color: 'var(--rt-heading)' }}>{formatHours(summary.total)}h</strong> tracked
          {summary.missing ? (
            <>
              {' · '}
              <strong style={{ color: 'var(--rt-danger)' }}>{formatHours(summary.missing)}h missing</strong> on {summary.incompleteDays} day{summary.incompleteDays === 1 ? '' : 's'}
            </>
          ) : (
            ' · nothing missing'
          )}
          {futureDays > 0 && (
            <>
              {' · '}
              <span style={{ color: FORECAST }}>{formatHours(forecastAhead)}h forecast</span> for the rest of the month
            </>
          )}
        </Typography>
      )}

      {planning && changes.length > 0 && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap', px: 2, py: 1, mb: 1.5, borderRadius: 2, bgcolor: 'var(--rt-forecast-soft)', color: 'var(--rt-forecast-ink)' }}>
          <Typography sx={{ fontSize: 14, fontWeight: 700 }}>
            {changes.length} unsaved change{changes.length === 1 ? '' : 's'}
          </Typography>
          <Box sx={{ flex: 1 }} />
          <Button color="inherit" onClick={() => setDrafts(baseline)} disabled={saving} sx={{ textTransform: 'none', color: 'var(--rt-forecast-ink)' }}>
            Discard
          </Button>
          {/* Stesso verde mostrato finora (override.css), con testo bianco anche nel tema scuro */}
          <Button variant="contained" color="success" onClick={saveForecast} disabled={saving} sx={{ textTransform: 'none', fontWeight: 700, boxShadow: 'none', bgcolor: GREEN, minWidth: 130 }}>
            {saving ? <CircularProgress size={18} color="inherit" /> : 'Save forecast'}
          </Button>
        </Box>
      )}

      {error && (
        <Alert severity="error" sx={{ mb: 1.5 }}>
          {error}
        </Alert>
      )}

      {!loading && monthData && !rows.length ? (
        <Alert severity="info">You have no projects and no hours this month.</Alert>
      ) : (
        <MyMonthTable
          monthDays={monthDays}
          yearMonth={yearMonth}
          rows={rows}
          absences={absences}
          loading={loading}
          planning={planning}
          drafts={drafts}
          baseline={baseline}
          onDraftChange={(pid, day, value) => setDrafts((d) => ({ ...d, [draftKey(pid, day)]: value }))}
          onOpenDay={openDay}
        />
      )}

      {/* Assenze del mese */}
      {absenceList.length > 0 && (
        <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 0.75, mt: 1.5 }}>
          <EventBusyIcon sx={{ fontSize: 18, color: 'var(--rt-danger)' }} />
          <Typography variant="body2" sx={{ fontWeight: 600, color: 'var(--rt-heading)', mr: 0.5 }}>
            Absences
          </Typography>
          {absenceList.map((a) => (
            <Chip
              key={a.id}
              size="small"
              label={`${parseLocalDate(String(a.date).slice(0, 10)).toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short' })} · ${a.full_day ? 'full day' : 'partial'}`}
              onDelete={() => setAbsenceToDelete(a)}
              sx={{ bgcolor: a.full_day ? STATUS_STYLE.absence.bg : STATUS_STYLE.partial.bg, color: a.full_day ? 'var(--rt-danger-dark)' : 'var(--rt-amber-2)', fontWeight: 600 }}
            />
          ))}
        </Box>
      )}

      {/* Legenda */}
      <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 2, mt: 1.5, color: 'text.secondary' }}>
        <Typography variant="caption">{planning ? 'Type hours in the blue cells.' : 'Click a day to open it in Track hours.'}</Typography>
        <Legend color={STATUS_STYLE.under.bg} label="Fewer than 8h" />
        <Legend color={STATUS_STYLE.missing.bg} label="0 = no hours" />
        <Legend color={STATUS_STYLE.partial.bg} label="Partial absence" />
        <Legend color={STATUS_STYLE.absence.bg} label="OFF = full-day absence" />
        <Typography variant="caption">
          <span style={{ color: FORECAST, fontStyle: 'italic' }}>Blue italic</span> = forecast
        </Typography>
      </Box>

      <Dialog open={!!pendingAction} onClose={() => setPendingAction(null)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 800 }}>Discard forecast changes?</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            You have {changes.length} unsaved change{changes.length === 1 ? '' : 's'}. They will be lost.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button color="inherit" onClick={() => setPendingAction(null)} sx={{ textTransform: 'none' }}>
            Keep editing
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={() => {
              const action = pendingAction;
              setDrafts(baseline);
              setPendingAction(null);
              action();
            }}
            sx={{ textTransform: 'none', fontWeight: 700, boxShadow: 'none' }}
          >
            Discard
          </Button>
        </DialogActions>
      </Dialog>

      <AbsenceDaysDialog open={absenceDaysOpen} userId={uid} initialDate={new Date(year, month - 1, 1)} onClose={() => setAbsenceDaysOpen(false)} onSaved={absenceDaysSaved} />

      <Dialog open={!!absenceToDelete} onClose={() => !deletingAbsence && setAbsenceToDelete(null)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 800 }}>Remove this absence?</DialogTitle>
        <DialogContent>
          {absenceToDelete && (
            <Typography variant="body2">
              {parseLocalDate(String(absenceToDelete.date).slice(0, 10)).toLocaleDateString('en-GB', { weekday: 'long', day: 'numeric', month: 'long' })} ·{' '}
              {absenceToDelete.full_day ? 'full day' : 'partial'}
            </Typography>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button color="inherit" onClick={() => setAbsenceToDelete(null)} disabled={deletingAbsence} sx={{ textTransform: 'none' }}>
            Cancel
          </Button>
          <Button variant="contained" color="error" onClick={confirmDeleteAbsence} disabled={deletingAbsence} sx={{ textTransform: 'none', fontWeight: 700, boxShadow: 'none' }}>
            {deletingAbsence ? <CircularProgress size={18} color="inherit" /> : 'Remove'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={!!snack} autoHideDuration={4000} onClose={() => setSnack(null)} message={snack} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }} />
    </PageContainer>
  );
}
